import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { PostgrestResponse, SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../../../../supabaseConfig';

// Tipo auxiliar para lidar com incompatibilidades de versão do PostgrestFilterBuilder
type GenericFilterBuilder = PostgrestFilterBuilder<any, any, any[]>;

/**
 * Service base para operações CRUD com isolamento multi-tenant automático
 * Todas as operações são automaticamente filtradas por organização
 */
export abstract class BaseMultiTenantService<T extends Record<string, any>> {
     protected client: SupabaseClient;
     protected tableName: string;
     protected searchableColumns: string[];
     protected organizationIdField: string;

     constructor(tableName: string, searchableColumns: string[], organizationIdField: string = 'fk_id_organizacao') {
          this.client = supabase;
          this.tableName = tableName;
          this.searchableColumns = searchableColumns;
          this.organizationIdField = organizationIdField;
     }

     /**
      * Aplica filtro automático de organização em todas as queries
      */
     protected applyOrganizationFilter(query: GenericFilterBuilder, organizationId: number): GenericFilterBuilder {
          return query.eq(this.organizationIdField, organizationId) as GenericFilterBuilder;
     }

     /**
      * Busca todos os registros com paginação, filtros e isolamento multi-tenant
      */
     async getAll(organizationId: number, page: number, pageSize: number, sortModel?: GridSortModel, filterModel?: GridFilterModel): Promise<PostgrestResponse<T>> {
          let query = this.client.from(this.tableName).select('*', { count: 'exact' }).is('deleted_at', null) as unknown as GenericFilterBuilder;

          // Aplicar isolamento multi-tenant
          query = this.applyOrganizationFilter(query, organizationId);

          // Aplicar busca rápida global
          if (filterModel?.quickFilterValues?.length) {
               query = this.applyQuickSearch(query, filterModel.quickFilterValues[0]);
          }

          // Aplicar filtros de coluna
          if (filterModel?.items?.length) {
               query = this.applyFilters(query, filterModel.items);
          }

          // Aplicar ordenação
          if (sortModel?.length) {
               const { field, sort } = sortModel[0];
               query = query.order(field, { ascending: sort === 'asc' });
          }

          // Aplicar paginação
          const from = page * pageSize;
          query = query.range(from, from + pageSize - 1);

          return (await query) as unknown as PostgrestResponse<T>;
     }

     /**
      * Busca todos os registros ativos com paginação e isolamento multi-tenant
      */
     async getAllActivePaginated(organizationId: number, page: number, pageSize: number, sortModel?: GridSortModel, filterModel?: GridFilterModel): Promise<PostgrestResponse<T>> {
          let query = this.client.from(this.tableName).select('*', { count: 'exact' }) as unknown as GenericFilterBuilder;

          // Aplicar isolamento multi-tenant
          query = this.applyOrganizationFilter(query, organizationId);

          // Aplicar busca rápida global
          if (filterModel?.quickFilterValues?.length) {
               query = this.applyQuickSearch(query, filterModel.quickFilterValues[0]);
          }

          // Aplicar filtros de coluna
          if (filterModel?.items?.length) {
               query = this.applyFilters(query, filterModel.items);
          }

          // Aplicar ordenação
          if (sortModel?.length) {
               const { field, sort } = sortModel[0];
               query = query.order(field, { ascending: sort === 'asc' });
          }

          // Aplicar paginação
          const from = page * pageSize;
          query = query.range(from, from + pageSize - 1);

          return (await query.is('deleted_at', null)) as unknown as PostgrestResponse<T>;
     }

     /**
      * Busca todos os registros ativos sem paginação (para selects/dropdowns)
      */
     async getAllActive(organizationId: number): Promise<PostgrestResponse<T>> {
          let query = this.client.from(this.tableName).select('*').is('deleted_at', null) as unknown as GenericFilterBuilder;

          // Aplicar isolamento multi-tenant
          query = this.applyOrganizationFilter(query, organizationId);

          return (await query) as unknown as PostgrestResponse<T>;
     }

     /**
      * Busca um registro por ID com isolamento multi-tenant
      */
     async getById(id: number, organizationId: number): Promise<PostgrestResponse<T>> {
          let query = this.client.from(this.tableName).select('*').eq('id', id) as unknown as GenericFilterBuilder;

          // Aplicar isolamento multi-tenant
          query = this.applyOrganizationFilter(query, organizationId);

          return (await query.single()) as unknown as PostgrestResponse<T>;
     }

     /**
      * Cria um novo registro com isolamento multi-tenant automático
      */
     async create(data: Partial<T>, organizationId: number, userId?: number): Promise<any> {
          const recordData = {
               ...data,
               [this.organizationIdField]: organizationId,
               created_at: new Date().toISOString(),
               ...(userId && { created_by: userId }),
          };

          return await this.client.from(this.tableName).insert(recordData).select('*').single();
     }

     /**
      * Atualiza um registro com verificação de isolamento multi-tenant
      */
     async update(id: number, data: Partial<T>, organizationId: number, userId?: number): Promise<any> {
          const updateData = {
               ...data,
               updated_at: new Date().toISOString(),
               ...(userId && { updated_by: userId }),
          };

          return await this.client.from(this.tableName).update(updateData).eq('id', id).eq(this.organizationIdField, organizationId).select('*').single();
     }

     /**
      * Remove um registro fisicamente com verificação de isolamento multi-tenant
      */
     async remove(id: number, organizationId: number): Promise<{ error: any }> {
          const { error } = await this.client.from(this.tableName).delete().eq('id', id).eq(this.organizationIdField, organizationId);

          return { error };
     }

     /**
      * Exclusão lógica (soft delete) com verificação de isolamento multi-tenant
      */
     async softDelete(id: number, organizationId: number, userId?: number): Promise<{ error: any }> {
          const data: any = {
               deleted_at: new Date().toISOString(),
               ...(userId && { deleted_by: userId }),
          };

          const { error } = await this.client.from(this.tableName).update(data).eq('id', id).eq(this.organizationIdField, organizationId);

          return { error };
     }

     /**
      * Aplica busca rápida em múltiplas colunas
      */
     protected applyQuickSearch(query: GenericFilterBuilder, searchTerm: string): GenericFilterBuilder {
          if (!searchTerm) return query;

          const conditions = this.searchableColumns.map((column) => `${column}.ilike.%${searchTerm}%`);
          return query.or(conditions.join(',')) as GenericFilterBuilder;
     }

     /**
      * Aplica filtros específicos de coluna
      */
     protected applyFilters(query: GenericFilterBuilder, filters: GridFilterModel['items']): GenericFilterBuilder {
          filters.forEach((filter) => {
               // Processa operadores que não necessitam de valor
               if (filter.operator !== 'isEmpty' && filter.operator !== 'isNotEmpty' && !filter.value) return;

               switch (filter.operator) {
                    case 'equals':
                         query = query.eq(filter.field, filter.value);
                         break;
                    case 'contains':
                         query = query.ilike(filter.field, `%${filter.value}%`);
                         break;
                    case 'startsWith':
                         query = query.ilike(filter.field, `${filter.value}%`);
                         break;
                    case 'endsWith':
                         query = query.ilike(filter.field, `%${filter.value}`);
                         break;
                    case 'isEmpty':
                         query = query.is(filter.field, null);
                         break;
                    case 'isNotEmpty':
                         query = query.not(filter.field, 'is', null);
                         break;
                    case 'greaterThan':
                         query = query.gt(filter.field, filter.value);
                         break;
                    case 'lessThan':
                         query = query.lt(filter.field, filter.value);
                         break;
                    case 'greaterThanOrEqual':
                         query = query.gte(filter.field, filter.value);
                         break;
                    case 'lessThanOrEqual':
                         query = query.lte(filter.field, filter.value);
                         break;
               }
          });

          return query;
     }
}
